import {
  DragEvent,
  ReactNode,
  SyntheticEvent,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from 'react';
import {
  FileReference,
  FileUploadRequest,
  uploadFiles
} from '../../api/upload';
import { guid } from '../../form-generator/validation/utils';
import isFileAccepted from '../../utils/isFileAccepted';
import { delay } from '../../utils/utils';

type Options = {
  files: Array<FileReference>;
  accept: Array<string>;
  onChange: (files: Array<FileReference>) => void;
  onError: (error: Error) => void;
};

type FileUploadConfig = {
  uploadFiles: (files: FileUploadRequest[]) => Promise<FileReference[]>;
  removeFile: (file: FileReference) => void;
};

const FileUploaderConfigContext = createContext<FileUploadConfig>({
  uploadFiles,
  removeFile: () => {}
});

export function FileUploaderConfig({
  // eslint-disable-next-line @typescript-eslint/no-shadow
  uploadFiles,
  removeFile,
  children
}: FileUploadConfig & { children: ReactNode }) {
  const value = useMemo(
    () => ({ uploadFiles, removeFile }),
    [uploadFiles, removeFile]
  );

  return (
    <FileUploaderConfigContext.Provider value={value}>
      {children}
    </FileUploaderConfigContext.Provider>
  );
}

export type FileReadyState =
  | 'pending'
  | 'uploading'
  | 'uploaded'
  | 'rejected'
  | 'failed';

type FileItemState = {
  state: FileReadyState;
  file: FileUploadRequest;
};

type State = Array<FileItemState>;

type Action =
  | { type: 'uploading' }
  | { type: 'uploaded'; files: Array<FileReference> }
  | { type: 'failed'; error: Error }
  | { type: 'add'; files: Array<FileUploadRequest> }
  | { type: 'remove'; fileName: string }
  | { type: 'clear-rejected-files' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'uploading': {
      return state.map(file => {
        if (file.state === 'pending') {
          return {
            ...file,
            state: 'uploading'
          };
        }

        return file;
      });
    }

    case 'uploaded': {
      return state.map(file => {
        const uploaded = !!action.files.find(
          x => x.fileName === file.file.name
        );
        if (file.state === 'uploading' && uploaded) {
          return {
            ...file,
            state: 'uploaded'
          };
        }

        return file;
      });
    }

    case 'failed': {
      return state.map(file => {
        if (file.state === 'uploading') {
          return {
            ...file,
            state: 'failed'
          };
        }

        return file;
      });
    }

    case 'add': {
      return state.concat(
        action.files.map(file => {
          return {
            file,
            state: file.isValid ? 'pending' : 'rejected'
          };
        })
      );
    }

    case 'remove': {
      return state.filter(file => file.file.name !== action.fileName);
    }

    case 'clear-rejected-files': {
      return state.filter(file => file.state !== 'rejected');
    }

    default:
      return state;
  }
}

function isSmallEnough(file: File) {
  return file.size < 20971520; // 20 MB
}

function fromEvent(event: any): Array<File> {
  // eslint-disable-next-line no-extra-boolean-cast
  if (!!event.dataTransfer) {
    return Array.from((event as DragEvent).dataTransfer.files);
  }

  const target = event.target as HTMLInputElement;
  if (target && target.files) {
    return Array.from(target.files);
  }

  return [];
}

function useFileUploads({ files, accept, onChange, onError }: Options) {
  const [state, dispatch] = useReducer(reducer, []);
  const filesRef = useRef(files);
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const { uploadFiles, removeFile } = useContext(FileUploaderConfigContext);

  // Make sure that the onChange is called with the correct
  // files when a upload started after another finishes first.
  //
  // Todo: Investigate potential timing issues and do this
  // differently.
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    const pendingFiles = state.filter(file => file.state === 'pending');

    if (pendingFiles.length === 0) {
      return;
    }

    const uploadPendingFiles = async () => {
      try {
        dispatch({ type: 'uploading' });

        await delay(500);

        const fileReferences = await uploadFiles(
          pendingFiles.map(file => file.file)
        );

        const fileReferenceByName = fileReferences.reduce<{
          [key: string]: FileReference;
        }>((byName, fileReference) => {
          byName[fileReference.name] = fileReference;
          return byName;
        }, {});

        dispatch({
          type: 'uploaded',
          files: fileReferences
        });

        onChange(
          filesRef.current.map(file => {
            const fileReference = fileReferenceByName[file.name] || {};
            const reference = file.reference || fileReference.reference;
            return {
              ...file,
              reference,
              mimeType: file.mimeType || fileReference.mimeType,
              fileSize: file.fileSize || fileReference.fileSize,
              fileId: file.fileId || fileReference.fileId,
              fileName: file.fileName || fileReference.fileName,
              preferredUsername:
                file.preferredUsername || fileReference.preferredUsername
            };
          })
        );
      } catch (error: any) {
        if (error instanceof Error) {
          onError(error);
          dispatch({ type: 'failed', error });
        }
      }
    };

    uploadPendingFiles();
  }, [state]); // eslint-disable-line

  const handleAdd = (event: SyntheticEvent) => {
    const newFiles = fromEvent(event).map(file => {
      const originalName = file.name;
      return {
        name: originalName,
        file,
        isValid: isFileAccepted(file, accept) && isSmallEnough(file)
      };
    });

    dispatch({ type: 'add', files: newFiles });

    const lastFile = files.length > 0 ? files[files.length - 1] : null;

    // Todo: notify changes elsewhere
    onChange(
      files.concat(
        newFiles
          .filter(file => file.isValid)
          .map(file => ({
            _id: lastFile && lastFile._id ? lastFile._id + 1 : guid(),
            name: file.name,
            originalName: file.file.name,
            reference: null,
            mimeType: null,
            url: '',
            fileSize: null,
            timestamp: null,
            fileId: '',
            fileName: '',
            preferredUsername: ''
          }))
      )
    );
  };

  const handleRemove = (fileName: string) => {
    dispatch({ type: 'remove', fileName });
    const removedFile = files.find(file => file.name === fileName);

    onChange(files.filter(file => file.name !== fileName));

    if (removedFile) {
      removeFile(removedFile);
    }
  };

  return {
    onAdd: handleAdd,
    onRemove: handleRemove,
    uploadFailed: state.some(file => file.state === 'failed'),
    hasRejectedFiles: state.some(file => file.state === 'rejected'),
    clearRejectedFiles: () => dispatch({ type: 'clear-rejected-files' }),
    readyStates: state.reduce<{ [key: string]: FileReadyState }>(
      (states, file) => {
        states[file.file.name] = file.state;
        return states;
      },
      {}
    )
  };
}

export default useFileUploads;
